import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Moon, Sun, Menu, X, Zap, LogIn, LogOut, User, Pencil, Shield, MapPin, Coffee } from 'lucide-react';
import { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useEmployee } from '../contexts/EmployeeContext';
import { useAuth } from '../contexts/AuthContext';
import { Employee } from '../types/employee';

const MAX_RESULTS = 8;

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { employees, searchEmployees } = useEmployee();
  const { user, employeeProfile, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isDirectoryPage = location.pathname === '/';

  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownResults, setDropdownResults] = useState<Employee[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  // Clear search when navigating between pages
  useEffect(() => {
    setSearchTerm('');
    setIsDropdownOpen(false);
    setActiveIndex(-1);
    setDropdownResults([]);
  }, [location.pathname]);

  // Close user menu and dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
      const target = e.target as Node;
      const outsideDesktop = !desktopSearchRef.current?.contains(target);
      const outsideMobile = !mobileSearchRef.current?.contains(target);
      if (outsideDesktop && outsideMobile) {
        setIsDropdownOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filterEmployees = useCallback((term: string): Employee[] => {
    if (!term.trim()) return [];
    const lower = term.toLowerCase();
    return employees.filter(e =>
      e.name.toLowerCase().includes(lower) ||
      e.role.toLowerCase().includes(lower) ||
      e.location.city.toLowerCase().includes(lower) ||
      e.location.country.toLowerCase().includes(lower) ||
      e.department.toLowerCase().includes(lower) ||
      e.employer.toLowerCase().includes(lower)
    );
  }, [employees]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (isDirectoryPage) {
      searchEmployees(value);
    } else {
      const results = filterEmployees(value);
      setDropdownResults(results);
      setIsDropdownOpen(value.trim().length > 0);
      setActiveIndex(-1);
    }
  };

  const selectResult = (employee: Employee) => {
    setSearchTerm('');
    setIsDropdownOpen(false);
    setActiveIndex(-1);
    setDropdownResults([]);
    setIsMobileMenuOpen(false);
    navigate(`/employee/${employee.id}`);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen) return;
    const visibleResults = dropdownResults.slice(0, MAX_RESULTS);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % visibleResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? visibleResults.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < visibleResults.length) {
        selectResult(visibleResults[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsDropdownOpen(false);
      setActiveIndex(-1);
      desktopInputRef.current?.blur();
      mobileInputRef.current?.blur();
    }
  };

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    await signOut();
    navigate('/');
  };

  const displayName = employeeProfile?.name || user?.user_metadata?.full_name || user?.email || '';
  const avatarUrl = employeeProfile?.avatar_url || user?.user_metadata?.avatar_url || null;
  const profileSlug = employeeProfile?.slug;

  const visibleResults = dropdownResults.slice(0, MAX_RESULTS);
  const extraCount = dropdownResults.length - MAX_RESULTS;

  const SearchDropdown = () => (
    <div className="absolute left-0 top-full mt-1 w-72 card shadow-xl z-50 overflow-hidden">
      {visibleResults.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <p className="text-xs text-bolt-textSubtle">No employees found for "{searchTerm}"</p>
        </div>
      ) : (
        <>
          <ul role="listbox" aria-label="Employee search results">
            {visibleResults.map((emp, idx) => (
              <li
                key={emp.id}
                role="option"
                aria-selected={idx === activeIndex}
                id={`search-result-${idx}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => { e.preventDefault(); selectResult(emp); }}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                  idx === activeIndex ? 'bg-bolt-surfaceHover' : 'hover:bg-bolt-surfaceHover'
                }`}
              >
                {emp.avatar ? (
                  <img
                    src={emp.avatar}
                    alt={emp.name}
                    className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-bolt-border"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-primary-600/20 flex items-center justify-center flex-shrink-0 border border-bolt-border">
                    <User className="h-3.5 w-3.5 text-primary-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-bolt-text truncate">{emp.name}</p>
                  <p className="text-xs text-bolt-textMuted truncate">{emp.role}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <MapPin className="h-3 w-3 text-bolt-textSubtle" />
                  <span className="text-xs text-bolt-textSubtle truncate max-w-20">{emp.location.city}</span>
                </div>
              </li>
            ))}
          </ul>
          {extraCount > 0 && (
            <div className="px-3 py-2 border-t border-bolt-border/60">
              <p className="text-xs text-bolt-textSubtle text-center">{extraCount} more result{extraCount > 1 ? 's' : ''} — refine your search</p>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <header className="bg-bolt-surface border-b border-bolt-border sticky top-0 z-50 backdrop-blur-sm bg-opacity-95">
      <div className="container-custom">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center shadow-sm group-hover:bg-primary-500 transition-colors">
              <Zap className="h-4 w-4 text-white" fill="currentColor" />
            </div>
            <span className="text-bolt-text font-semibold text-base tracking-tight">
              StackBlitz Team
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-3">
            <div className="relative" ref={desktopSearchRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
              <input
                ref={desktopInputRef}
                type="text"
                role="combobox"
                aria-expanded={isDropdownOpen}
                aria-autocomplete="list"
                aria-activedescendant={activeIndex >= 0 ? `search-result-${activeIndex}` : undefined}
                placeholder="Search employees..."
                className="form-input pl-9 w-56 h-8 text-sm"
                value={searchTerm}
                onChange={e => handleSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (!isDirectoryPage && searchTerm.trim()) setIsDropdownOpen(true);
                }}
              />
              {isDropdownOpen && !isDirectoryPage && <SearchDropdown />}
            </div>

            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover border border-transparent hover:border-bolt-border transition-all"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(v => !v)}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-bolt-border hover:bg-bolt-surfaceHover transition-all"
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-primary-600/30 flex items-center justify-center">
                      <User className="h-3 w-3 text-primary-400" />
                    </div>
                  )}
                  <span className="text-xs font-medium text-bolt-textMuted max-w-32 truncate">{displayName}</span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-44 card py-1 shadow-xl z-50">
                    {profileSlug && (
                      <MenuLink to={`/employee/${profileSlug}`} icon={<User className="h-3.5 w-3.5" />} onClick={() => setIsUserMenuOpen(false)}>
                        View profile
                      </MenuLink>
                    )}
                    <MenuLink to="/profile/edit" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => setIsUserMenuOpen(false)}>
                      Edit profile
                    </MenuLink>
                    <MenuLink to="/meet" icon={<Coffee className="h-3.5 w-3.5" />} onClick={() => setIsUserMenuOpen(false)}>
                      Meet someone
                    </MenuLink>
                    {isAdmin && (
                      <MenuLink to="/admin" icon={<Shield className="h-3.5 w-3.5" />} onClick={() => setIsUserMenuOpen(false)}>
                        Admin
                      </MenuLink>
                    )}
                    <div className="border-t border-bolt-border my-1" />
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-bolt-textMuted hover:text-error-400 hover:bg-bolt-surfaceHover transition-colors"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-bolt-textMuted hover:text-bolt-text border border-bolt-border hover:bg-bolt-surfaceHover transition-all"
              >
                <LogIn className="h-3.5 w-3.5" />
                Sign in
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 rounded-lg text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover transition-all"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-bolt-surface border-t border-bolt-border py-3 px-4 animate-fade-in space-y-3">
          <div className="relative" ref={mobileSearchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle pointer-events-none" />
            <input
              ref={mobileInputRef}
              type="text"
              role="combobox"
              aria-expanded={isDropdownOpen}
              aria-autocomplete="list"
              aria-activedescendant={activeIndex >= 0 ? `search-result-${activeIndex}` : undefined}
              placeholder="Search employees..."
              className="form-input pl-9 w-full"
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (!isDirectoryPage && searchTerm.trim()) setIsDropdownOpen(true);
              }}
            />
            {isDropdownOpen && !isDirectoryPage && <SearchDropdown />}
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 p-2 rounded-lg text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover transition-all text-sm"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </button>
            {user ? (
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 p-2 rounded-lg text-bolt-textMuted hover:text-error-400 transition-all text-sm"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 p-2 rounded-lg text-bolt-textMuted hover:text-bolt-text transition-all text-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LogIn className="h-4 w-4" />
                Sign in
              </Link>
            )}
          </div>
          {user && (
            <div className="flex items-center gap-2 border-t border-bolt-border pt-3">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary-600/30 flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-primary-400" />
                </div>
              )}
              <span className="text-xs text-bolt-textMuted truncate">{displayName}</span>
              <Link to="/profile/edit" className="ml-auto text-xs text-primary-400 hover:text-primary-300" onClick={() => setIsMobileMenuOpen(false)}>
                Edit profile
              </Link>
            </div>
          )}
          {user && (
            <Link
              to="/meet"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover transition-all text-sm"
            >
              <Coffee className="h-4 w-4" />
              Meet someone
            </Link>
          )}
        </div>
      )}
    </header>
  );
};

const MenuLink = ({ to, icon, onClick, children }: { to: string; icon: React.ReactNode; onClick: () => void; children: React.ReactNode }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-2.5 px-3 py-2 text-xs text-bolt-textMuted hover:text-bolt-text hover:bg-bolt-surfaceHover transition-colors"
  >
    <span className="text-bolt-textSubtle">{icon}</span>
    {children}
  </Link>
);

export default Header;
